/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * call.7ion.org/join/<code>: a call by link for someone without iON
 * (decision 49, ion-spec calls/GUEST-LINK.md). The guest gives a name, checks
 * their camera and microphone, and the guest service issues a temporary
 * account for this one call; once the creator lets them in they are in the
 * call like anyone else. The guest sees nothing of the room but the call:
 * neither its chat nor any account settings.
 */

import {
  type ComponentType,
  type FC,
  type ReactNode,
  type SVGAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@vector-im/compound-web";
import {
  CloseIcon,
  EndCallIcon,
  ErrorSolidIcon,
  GroupIcon,
  LinkIcon,
  TimeIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import { type MatrixClient } from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";

import styles from "./GuestJoinPage.module.css";
import {
  callLinksApiFromConfig,
  CallLinkError,
  type CallLinkErrorKind,
  type CallLinkInfo,
  type CallLinksApi,
  type GuestCredentials,
  downloadIonUrl,
  isValidCallLinkCode,
} from "./callLinksApi";
import {
  type Admission,
  awaitAdmission,
  createGuestClient,
  disposeGuestClient,
} from "./guestSession";
import { FullScreenView, ErrorPage, LoadingPage } from "../FullScreenView";
import { ErrorView } from "../ErrorView";
import { LobbyView } from "../room/LobbyView";
import { CallView } from "../room/CallView";
import {
  CallTerminatedMessage,
  useLoadGroupCall,
} from "../room/useLoadGroupCall";
import { useMuteStates } from "../state/useMuteStates";
import { useBehavior } from "../useBehavior";
import { E2eeType } from "../e2ee/e2eeType";
import { InputField } from "../input/Input";
import { HeaderStyle, useUrlParams } from "../UrlParams";
import { usePageTitle } from "../usePageTitle";
import { HostBridgeProvider, nullHostBridge } from "../HostBridge";
import { LeaveToHomeProvider } from "../LeaveToHomeContext";
import { useGraylog } from "../logging/Graylog";

type MuteChoice = { audioEnabled: boolean; videoEnabled: boolean };

type Phase =
  | { kind: "loading" }
  | { kind: "unavailable"; reason: CallLinkErrorKind }
  | { kind: "lobby"; link: CallLinkInfo }
  | { kind: "waiting"; link: CallLinkInfo; client: MatrixClient }
  | { kind: "rejected" }
  | {
      kind: "call";
      link: CallLinkInfo;
      client: MatrixClient;
      muteChoice: MuteChoice;
    }
  | { kind: "ended" };

const NAME_KEY = "ion-guest-name";
const NAME_MAX_LENGTH = 64;
/** Time for the call to tell the others we left before the account is let go. */
const DISPOSE_DELAY_MS = 5000;
const NO_VIA_SERVERS: string[] = [];

// The guest's account is the service's, never theirs to edit
const guestHostBridge = { ...nullHostBridge, supportsProfileChanges: false };

function loadName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Only a convenience
  }
}

const errorKind = (e: unknown): CallLinkErrorKind =>
  e instanceof CallLinkError ? e.kind : "failed";

interface Props {
  /** For tests: the guest service, and how to sign in with what it issues. */
  api?: CallLinksApi;
  connect?: (credentials: GuestCredentials) => Promise<MatrixClient>;
}

export const GuestJoinPage: FC<Props> = ({
  api: apiOverride,
  connect = createGuestClient,
}) => {
  const { t } = useTranslation();
  const { code } = useParams();
  const api = useMemo(
    () => apiOverride ?? callLinksApiFromConfig(),
    [apiOverride],
  );

  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  // Load the link
  useEffect(() => {
    if (!isValidCallLinkCode(code)) {
      setPhase({ kind: "unavailable", reason: "not_found" });
      return;
    }
    let cancelled = false;
    setPhase({ kind: "loading" });
    api.getLink(code).then(
      (link) => !cancelled && setPhase({ kind: "lobby", link }),
      (e) => {
        logger.warn("Could not load the call link", e);
        if (!cancelled) setPhase({ kind: "unavailable", reason: errorKind(e) });
      },
    );
    return (): void => {
      cancelled = true;
    };
  }, [api, code, attempt]);

  // The guest's client, whatever phase holds it, is let go with the page
  const clientRef = useRef<MatrixClient | null>(null);
  useEffect(
    () => (): void => {
      if (clientRef.current) disposeGuestClient(clientRef.current);
    },
    [],
  );

  const [name, setName] = useState(loadName);
  const graylog = useGraylog();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<CallLinkErrorKind | null>(null);
  const muteChoice = useRef<MuteChoice>({
    audioEnabled: true,
    videoEnabled: true,
  });
  const onMuteChoice = useCallback((choice: MuteChoice): void => {
    muteChoice.current = choice;
  }, []);

  const onJoin = useCallback(
    (link: CallLinkInfo) => {
      const displayName = name.trim();
      if (!code || displayName === "" || joining) return;
      saveName(displayName);
      setJoining(true);
      setJoinError(null);
      (async (): Promise<void> => {
        const credentials = await api.join(code, displayName);
        const client = await connect(credentials);
        clientRef.current = client;
        logger.info(`Guest ${client.getUserId()} signed in`);
        setPhase({ kind: "waiting", link, client });
      })()
        .catch((e) => {
          const kind = errorKind(e);
          logger.error("Guest could not join", e);
          if (kind === "failed")
            graylog.logError(
              "Guest could not join by link",
              e instanceof Error ? e.message : String(e),
            );
          if (kind === "full" || kind === "expired" || kind === "revoked")
            setPhase({ kind: "unavailable", reason: kind });
          else setJoinError(kind);
        })
        .finally(() => setJoining(false));
    },
    [api, code, connect, graylog, joining, name],
  );

  // Wait to be let in
  useEffect(() => {
    if (phase.kind !== "waiting") return;
    const { client, link } = phase;
    const abort = new AbortController();
    awaitAdmission(client, link.roomId, abort.signal).then(
      (admission: Admission) => {
        logger.info(`Guest admission: ${admission}`);
        if (admission === "admitted")
          setPhase({
            kind: "call",
            link,
            client,
            muteChoice: { ...muteChoice.current },
          });
        else {
          clientRef.current = null;
          disposeGuestClient(client);
          setPhase({ kind: "rejected" });
        }
      },
      (e) => {
        if (abort.signal.aborted) return;
        logger.error("Guest could not get into the room", e);
        setJoinError("failed");
        setPhase({ kind: "lobby", link });
      },
    );
    return (): void => abort.abort();
  }, [phase]);

  // Back to the start: the link is loaded afresh, and may no longer let anyone in
  const restart = useCallback(() => {
    setJoinError(null);
    setAttempt((n) => n + 1);
  }, []);

  const onCallEnded = useCallback(() => {
    const client = clientRef.current;
    clientRef.current = null;
    if (client)
      window.setTimeout(() => disposeGuestClient(client), DISPOSE_DELAY_MS);
    setPhase({ kind: "ended" });
  }, []);

  const title =
    phase.kind === "lobby" || phase.kind === "waiting" || phase.kind === "call"
      ? phase.link.title || t("guest_link.title_fallback")
      : undefined;
  usePageTitle(title);

  let body: ReactNode;
  switch (phase.kind) {
    case "loading":
      body = <LoadingPage />;
      break;
    case "unavailable":
      body = <UnavailableView reason={phase.reason} onRetry={restart} />;
      break;
    case "lobby":
    case "waiting":
      body = (
        <GuestLobby
          link={phase.link}
          title={title!}
          name={name}
          onNameChange={setName}
          waiting={phase.kind === "waiting"}
          joining={joining}
          joinError={joinError}
          onJoin={() => onJoin(phase.link)}
          onMuteChoice={onMuteChoice}
        />
      );
      break;
    case "rejected":
      body = (
        <GuestMessage
          Icon={CloseIcon}
          title={t("guest_link.rejected_title")}
          body={t("guest_link.rejected_body")}
        />
      );
      break;
    case "call":
      body = (
        <LeaveToHomeProvider value={onCallEnded}>
          <GuestCall
            client={phase.client}
            roomId={phase.link.roomId}
            muteChoice={phase.muteChoice}
          />
        </LeaveToHomeProvider>
      );
      break;
    case "ended":
      body = (
        <GuestMessage
          Icon={EndCallIcon}
          title={t("guest_link.ended_title")}
          body={t("guest_link.ended_body")}
          extra={
            <Button kind="secondary" onClick={restart}>
              {t("guest_link.rejoin_button")}
            </Button>
          }
        />
      );
      break;
  }

  return (
    <HostBridgeProvider value={guestHostBridge}>
      {/* Nowhere for a guest to go but this call */}
      <LeaveToHomeProvider value={null}>{body}</LeaveToHomeProvider>
    </HostBridgeProvider>
  );
};

interface GuestLobbyProps {
  link: CallLinkInfo;
  title: string;
  name: string;
  onNameChange: (name: string) => void;
  waiting: boolean;
  joining: boolean;
  joinError: CallLinkErrorKind | null;
  onJoin: () => void;
  onMuteChoice: (choice: MuteChoice) => void;
}

/**
 * The camera check before the call, with the guest's name; it stays up, camera
 * and all, while the guest waits to be let in.
 */
const GuestLobby: FC<GuestLobbyProps> = (props) => {
  const muteStates = useMuteStates();
  if (muteStates === null) return null;
  return <GuestLobbyWithMedia {...props} muteStates={muteStates} />;
};

const GuestLobbyWithMedia: FC<
  GuestLobbyProps & {
    muteStates: NonNullable<ReturnType<typeof useMuteStates>>;
  }
> = ({
  link,
  title,
  name,
  onNameChange,
  waiting,
  joining,
  joinError,
  onJoin,
  onMuteChoice,
  muteStates,
}) => {
  const { t } = useTranslation();
  const { header } = useUrlParams();
  const audioEnabled = useBehavior(muteStates.audio.enabled$);
  const videoEnabled = useBehavior(muteStates.video.enabled$);
  useEffect(() => {
    onMuteChoice({ audioEnabled, videoEnabled });
  }, [audioEnabled, videoEnabled, onMuteChoice]);

  const trimmed = name.trim();
  const enterLabel = waiting
    ? t("guest_link.waiting")
    : joining
      ? t("guest_link.joining")
      : t("guest_link.join_button");

  return (
    <LobbyView
      matrixInfo={{
        userId: "@guest",
        displayName: trimmed || "?",
        avatarUrl: "",
        roomId: link.roomId,
        roomName: title,
        roomAlias: null,
        roomAvatar: null,
        e2eeSystem: { kind: E2eeType.PER_PARTICIPANT },
      }}
      muteStates={muteStates}
      onEnter={onJoin}
      enterLabel={enterLabel}
      enterDisabled={trimmed === "" || joining}
      waitingForInvite={waiting}
      confineToRoom
      hideHeader={header !== HeaderStyle.Standard}
      participantCount={null}
      onShareClick={null}
      beforeEnter={
        waiting ? null : (
          <form
            className={styles.nameForm}
            onSubmit={(e) => {
              e.preventDefault();
              onJoin();
            }}
          >
            <InputField
              id="guestName"
              name="guestName"
              type="text"
              label={t("guest_link.name_label")}
              autoComplete="name"
              value={name}
              disabled={joining}
              onChange={(e) =>
                onNameChange(e.target.value.slice(0, NAME_MAX_LENGTH))
              }
            />
          </form>
        )
      }
      belowPreview={
        <div className={styles.below}>
          {joinError && (
            <Text className={styles.error} role="alert">
              {joinError === "rate_limited"
                ? t("guest_link.rate_limited")
                : t("guest_link.join_failed")}
            </Text>
          )}
          <Text size="sm" className={styles.invitedBy}>
            {t("guest_link.invited_by", {
              name: link.creator.displayName,
            })}
          </Text>
          <DownloadLink />
        </div>
      }
    />
  );
};

interface GuestCallProps {
  client: MatrixClient;
  roomId: string;
  muteChoice: MuteChoice;
}

/** The call itself, as in the app; leaving it ends at «Call ended». */
const GuestCall: FC<GuestCallProps> = ({ client, roomId, muteChoice }) => {
  const state = useLoadGroupCall(client, roomId, NO_VIA_SERVERS);
  switch (state.kind) {
    case "loaded":
      return (
        <CallView
          client={client}
          rtcSession={state.rtcSession}
          isPasswordlessUser={false}
          confineToRoom={false}
          preload={false}
          skipLobby
          initialMuteState={muteChoice}
        />
      );
    case "failed":
      // Removed from the call by its creator, most likely
      if (state.error instanceof CallTerminatedMessage)
        return (
          <GuestMessage
            Icon={state.error.icon}
            title={state.error.message}
            body={state.error.messageBody}
          />
        );
      return <ErrorPage error={state.error} />;
    default:
      return <LoadingPage />;
  }
};

const DownloadLink: FC<{ primary?: boolean }> = ({ primary }) => {
  const { t } = useTranslation();
  return primary ? (
    <Button
      as="a"
      kind="primary"
      href={downloadIonUrl()}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t("guest_link.download_ion")}
    </Button>
  ) : (
    <a
      className={styles.download}
      href={downloadIonUrl()}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t("guest_link.download_ion")}
    </a>
  );
};

interface GuestMessageProps {
  Icon: ComponentType<SVGAttributes<SVGElement>>;
  title: string;
  body?: string;
  extra?: ReactNode;
}

const GuestMessage: FC<GuestMessageProps> = ({ Icon, title, body, extra }) => (
  <FullScreenView>
    <ErrorView Icon={Icon} title={title}>
      {body && <p>{body}</p>}
      <div className={styles.actions}>
        <DownloadLink primary />
        {extra}
      </div>
    </ErrorView>
  </FullScreenView>
);

const UnavailableView: FC<{
  reason: CallLinkErrorKind;
  onRetry: () => void;
}> = ({ reason, onRetry }) => {
  const { t } = useTranslation();
  switch (reason) {
    case "expired":
      return (
        <GuestMessage
          Icon={TimeIcon}
          title={t("guest_link.expired_title")}
          body={t("guest_link.new_link_body")}
        />
      );
    case "revoked":
      return (
        <GuestMessage
          Icon={LinkIcon}
          title={t("guest_link.revoked_title")}
          body={t("guest_link.new_link_body")}
        />
      );
    case "not_found":
      return (
        <GuestMessage
          Icon={LinkIcon}
          title={t("guest_link.invalid_title")}
          body={t("guest_link.new_link_body")}
        />
      );
    case "full":
      return (
        <GuestMessage
          Icon={GroupIcon}
          title={t("guest_link.full_title")}
          body={t("guest_link.full_body")}
          extra={
            <Button kind="secondary" onClick={onRetry}>
              {t("guest_link.retry_button")}
            </Button>
          }
        />
      );
    default:
      return (
        <GuestMessage
          Icon={ErrorSolidIcon}
          title={t("guest_link.load_failed_title")}
          body={t("guest_link.load_failed_body")}
          extra={
            <Button kind="secondary" onClick={onRetry}>
              {t("guest_link.retry_button")}
            </Button>
          }
        />
      );
  }
};
